import React from 'react';
import PropTypes from 'prop-types';
import {
    WeezHeader,
    Button,
    Form,
    Card as NacreCard,
    Input,
    ToggleLabel,
    ContentCard,
    SelectLabel,
    Section,
    SelectHierarchy,
    TooltipHelp,
    Chip,
    ToastContext
} from '@weezevent/nacre';
import { SideBar } from '@weezevent/socium';

import { useTranslation } from 'react-i18next';
import { useForm } from '../../utils/validators';
import Style from './chart-list.module.css';
import Chart from '../../models/chart';
import { usePermissions } from '../../user/permissions';
import { useHistory } from 'react-router-dom';

export default function ChartDrawer({ chart = null, organization, open = false, depth = 0, onClickAway = () => {}, onSubmit = () => {} }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const permissions = usePermissions();
    const [selectedTypePlan, setSelectedTypePlan] = React.useState(null);
    let [form, setField, setForm] = useForm({ name: '', pacifa_key: null, prevent_orphans_seats: false, type: null, metadata: {} });

    const onSubmitDrawer = React.useCallback(
        data => {
            let chart_obj = new Chart(chart);
            chart_obj.name = data.name;
            if (!chart_obj.seatlib_data) {
                chart_obj['seatlib_data'] = {};
            }
            if (selectedTypePlan === 'club') {
                chart_obj.type = form.type;
                chart_obj.metadata = form.metadata;
            } else {
                chart_obj.type = 'default';
                chart_obj.metadata = {};
                setForm('type', 'default');
                setForm('metadata', { club_name: '' });
            }
            chart_obj.seatlib_data.prevent_orphans_seats = data.prevent_orphans_seats;
            chart_obj.pacifa_key = data.pacifa_key === '' ? null : data.pacifa_key;
            chart_obj
                .save({ organizationId: organization.id })
                .then(({ response }) => {
                    toast.success(t('ticket.charts.newChart.success'));
                    onSubmit();
                    history.push(`/ticket/O${organization.id}/charts/${response.uuid}/dashboard`);
                })
                .catch(e => {
                    toast.error(t('ticket.charts.newChart.failure'));
                });
        },
        [toast, organization, t, chart, onSubmit, form]
    );

    const handleClickToggle = React.useCallback(
        (e, { checked }) => {
            setField('prevent_orphans_seats', checked);
        },
        [setField]
    );

    React.useEffect(() => {
        if (chart) {
            setForm({ name: chart.name, prevent_orphans_seats: chart.seatlib_data.prevent_orphans_seats || false, pacifa_key: chart.pacifa_key });
        } else {
            setForm({ name: '', pacifa_key: null, prevent_orphans_seats: false, type: 'default', metadata: {} });
        }
    }, [chart, setForm]);

    return (
        <SideBar open={open} depth={depth} onClickAway={onClickAway} onClose={onClickAway} className={Style['side-bar-charts']}>
            <div>
                <WeezHeader title={chart ? t('ticket.charts.chart.editChart') : t('ticket.charts.newChart.title')} />

                <NacreCard className={Style['side-bar-content']}>
                    <ContentCard>
                        <Form>
                            <Form.Field>
                                <Form.Label label={t('ticket.charts.newChart.chartName')} />
                                <Input
                                    placeholder={t('ticket.charts.newChart.chartName')}
                                    labelPosition="right"
                                    value={form.name}
                                    onChange={event => setField('name', event.target.value)}
                                />
                            </Form.Field>
                            {permissions.isAdmin() && (
                                <>
                                    <div className={Style['chart-type-container']}>
                                        <Form.Label>{t('ticket.charts.settings.type-chart')}</Form.Label>
                                        <Chip primary text={t('ticket.common.admin')} />
                                    </div>
                                    <SelectLabel
                                        placeholder={t('ticket.charts.settings.type-chart-placeholder')}
                                        onChange={value => setSelectedTypePlan(value)}
                                        options={[
                                            { text: t('ticket.charts.settings.type-chart-default'), value: null },
                                            { text: t('ticket.charts.settings.type-chart-sport-club'), value: 'club' }
                                        ]}
                                        value={selectedTypePlan}
                                    />
                                    {selectedTypePlan === 'club' && (
                                        <Section>
                                            <Form.Label label={t('ticket.charts.settings.league')} />
                                            <SelectHierarchy
                                                searchable
                                                multi={false}
                                                widthIndent="10px"
                                                onChange={value => setField('type', value)}
                                                options={[
                                                    {
                                                        parent: null,
                                                        text: t('ticket.charts.settings.league_fr'),
                                                        value: null,
                                                        children: [
                                                            { key: 0, value: 'lfp_ligue_1', text: t('ticket.charts.settings.league_fr_1') },
                                                            { key: 0, value: 'lfp_ligue_2', text: t('ticket.charts.settings.league_fr_2') }
                                                        ]
                                                    }
                                                ]}
                                                value={form.type}
                                            />

                                            <Form.Field>
                                                <Form.Label optionalText={t('ticket.common.labelOptional')}>
                                                    {t('ticket.charts.settings.club-name')}
                                                    <TooltipHelp inlinePosition="left" title={t('ticket.charts.settings.club-name-tooltip')} />
                                                </Form.Label>
                                                <Form.Input onChange={(e, { value }) => setField('metadata', { club_name: value })} value={form?.metadata?.club_name} />
                                            </Form.Field>
                                        </Section>
                                    )}
                                </>
                            )}

                            <Form.Field>
                                <Form.Label label={t('ticket.charts.newChart.pacifaKey')} optionalText={t('ticket.common.labelOptional')} />
                                <Input
                                    placeholder={t('ticket.charts.newChart.pacifaKey')}
                                    labelPosition="right"
                                    value={form.pacifa_key}
                                    onChange={event => setField('pacifa_key', event.target.value)}
                                />
                            </Form.Field>

                            <ToggleLabel checked={form.prevent_orphans_seats} onChange={handleClickToggle}>
                                <Form.Label>{t('ticket.charts.newChart.preventOrphansSeats')}</Form.Label>
                                <Form.Sublabel>{t('ticket.charts.newChart.preventOrphansSeatsPlaceholder')}</Form.Sublabel>
                            </ToggleLabel>

                            <div>
                                <Button
                                    disabled={!form?.name || (selectedTypePlan === 'club' && form.type === 'default')}
                                    primary
                                    label={chart ? t('ticket.common.action.save') : t('ticket.common.action.create')}
                                    onClick={() => onSubmitDrawer(form)}
                                />
                            </div>
                        </Form>
                    </ContentCard>
                </NacreCard>
            </div>
        </SideBar>
    );
}

ChartDrawer.propTypes = {
    chart: PropTypes.node,
    organization: PropTypes.node,
    open: PropTypes.bool.isRequired,
    depth: PropTypes.number,
    onClickAway: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired
};
